import React, { useRef, useEffect } from 'react';
import Input from '../../ui/Input';
import Label from '../../ui/Label';
import Select from '../../ui/Select';

const InvoiceDetails = ({ 
  formData = {}, 
  handleInputChange = () => {}, 
  bankDetails = [], 
  searchClients, 
  suggestions = [], 
  showSuggestions = false, 
  clearSuggestions = () => {} 
}) => {
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        clearSuggestions();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef, clearSuggestions]);

  const handleNameChange = (e) => {
    const { value } = e.target;
    handleInputChange(e);
    if (searchClients) {
      searchClients(value);
    }
  };

  const handleSelectSuggestion = (client) => {
    formData.receiverName = client.name;
    formData.receiverIdNumber = client.idNumber;
    formData.receiverCity = client.city;
    formData.receiverAddress = client.address;
    formData.receiverMol = client.mol;
    formData.receiverPhoneNumber = client.phoneNumber || '';
    formData.receiverVatNumber = client.vatNumber || '';
    clearSuggestions();
    
    // Trigger an update since we mutated formData directly (mimicking old behavior)
    // In a real refactor, formData should be immutable, but we stick to existing logic.
    // The safest way is to dispatch a fake event to handleInputChange
    handleInputChange({ target: { name: 'receiverName', value: client.name } });
  };

  return (
    <div className="p-4 sm:p-6 border-b border-gray-100">
      <h3 className="text-[14px] font-bold text-gray-900 mb-4 sm:mb-5">Данни за фактура</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-x-6 gap-y-5">
        
        {/* Row 1 */}
        <div>
          <Label>Номер на фактура</Label>
          <Input 
            type="number" 
            name="invoiceNumber"
            placeholder="0123456789" 
            value={formData.invoiceNumber || ''}
            onChange={handleInputChange}
          />
        </div>
        <div className="relative" ref={wrapperRef}>
          <Label>Име</Label>
          <Input 
            type="text" 
            name="receiverName"
            placeholder="Дженерал Сълюшън ЕООД" 
            value={formData.receiverName || ''}
            onChange={handleNameChange}
            autoComplete="off"
          />
          {showSuggestions && suggestions && suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((client) => (
                <div
                  key={client.id}
                  className="px-4 py-3 cursor-pointer hover:bg-gray-50 text-[13px] text-gray-700 transition-colors border-b border-gray-50 last:border-0"
                  onClick={() => handleSelectSuggestion(client)}
                >
                  <div className="font-semibold text-gray-900">{client.name}</div>
                  {client.idNumber && <div className="text-gray-500 text-[12px] mt-0.5">ЕИК: {client.idNumber}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label>МОЛ</Label>
          <Input 
            type="text" 
            name="receiverMol"
            placeholder="Иван Иванов" 
            value={formData.receiverMol || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Row 2 */}
        <div>
          <Label>ЕИК</Label>
          <Input 
            type="text" 
            name="receiverIdNumber"
            placeholder="123456789"
            value={formData.receiverIdNumber || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Телефон</Label>
          <Input 
            type="tel" 
            name="receiverPhoneNumber"
            placeholder="0878000000" 
            value={formData.receiverPhoneNumber || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>ДДС номер</Label>
          <Input 
            type="text" 
            name="receiverVatNumber"
            placeholder="BG123456789"
            value={formData.receiverVatNumber || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Row 3 */}
        <div>
          <Label>Дата на създаване</Label>
          <Input 
            type="date" 
            name="creationDate"
            value={formData.creationDate || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Дата на падеж</Label>
          <Input 
            type="date" 
            name="dueDate"
            value={formData.dueDate || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Дата на данъчно събитие</Label>
          <Input 
            type="date" 
            name="taxEventDate"
            value={formData.taxEventDate || ''}
            onChange={handleInputChange}
          />
        </div>

        {/* Row 4 */}
        <div>
          <Label>Град</Label>
          <Input 
            type="text" 
            name="receiverCity"
            placeholder="София" 
            value={formData.receiverCity || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Адрес</Label>
          <Input 
            type="text" 
            name="receiverAddress"
            placeholder="Бул. Стамболийски 8" 
            value={formData.receiverAddress || ''}
            onChange={handleInputChange}
          />
        </div>
        <div>
          <Label>Плащане</Label>
          <Select 
            name="paymentMethod"
            value={formData.paymentMethod || 'CASH'}
            onChange={handleInputChange}
          >
            <option value="CASH">В брой</option>
            <option value="BANK_TRANSFER">Банков път</option>
          </Select>
        </div>

        {/* Conditional Bank Details */}
        {formData.paymentMethod === 'BANK_TRANSFER' && (
          <div className="2xl:col-span-3">
            <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-100 max-w-sm">
              <Label>Банкова сметка</Label>
              <Select
                name="issuerBankDetailsId"
                value={formData.issuerBankDetailsId || ''}
                onChange={handleInputChange}
              >
                <option value="" disabled>Изберете банкова сметка</option>
                {bankDetails && bankDetails.map(bd => (
                  <option key={bd.id} value={bd.id}>{bd.iban}</option>
                ))}
              </Select>
              
              {formData.issuerBankDetailsId && bankDetails && bankDetails.find(bd => bd.id === formData.issuerBankDetailsId) && (
                <div className="mt-3 text-[13px] text-gray-600 space-y-1">
                  <div><strong className="text-gray-900 font-semibold">Банка:</strong> {bankDetails.find(bd => bd.id === formData.issuerBankDetailsId).bank}</div>
                  <div><strong className="text-gray-900 font-semibold">Банков код:</strong> {bankDetails.find(bd => bd.id === formData.issuerBankDetailsId).bankCode}</div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default InvoiceDetails;
